import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Database } from '@/integrations/supabase/types';

type ThreadRow = Database['public']['Tables']['threads']['Row'];
type ThreadMessageRow = Database['public']['Tables']['thread_messages']['Row'];

export interface ThreadWithMessages extends ThreadRow {
  message_count: number;
  last_message_at: string | null;
  author_email: string | null;
}

export interface ThreadMessage extends ThreadMessageRow {
  author_email: string | null;
}

export const useThreads = () => {
  const [threads, setThreads] = useState<ThreadWithMessages[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchThreads = useCallback(async () => {
    if (!user) return;

    const { data: threadsData, error: threadsError } = await supabase
      .from('threads')
      .select('*')
      .order('updated_at', { ascending: false });

    if (threadsError) {
      console.error('Error fetching threads:', threadsError);
      setLoading(false);
      return;
    }

    // Pobierz liczbe wiadomosci i ostatnia wiadomosc dla kazdego watku
    const threadsWithMeta: ThreadWithMessages[] = await Promise.all(
      (threadsData || []).map(async (thread) => {
        const { count } = await supabase
          .from('thread_messages')
          .select('*', { count: 'exact', head: true })
          .eq('thread_id', thread.id);

        const { data: lastMsg } = await supabase
          .from('thread_messages')
          .select('created_at')
          .eq('thread_id', thread.id)
          .order('created_at', { ascending: false })
          .limit(1);

        return {
          ...thread,
          message_count: count || 0,
          last_message_at: lastMsg?.[0]?.created_at || null,
          author_email: null, // email bedzie z cache uzytkownikow
        };
      })
    );

    setThreads(threadsWithMeta);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Realtime subskrypcja
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('threads-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'threads' }, () => {
        fetchThreads();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'thread_messages' }, () => {
        fetchThreads();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchThreads]);

  const createThread = async (title: string, content: string) => {
    if (!user) return { error: 'Nie zalogowany' };

    const { error } = await supabase.from('threads').insert({
      user_id: user.id,
      title,
      content,
    });

    if (error) return { error: error.message };
    return { error: null };
  };

  const closeThread = async (threadId: string) => {
    const { error } = await supabase
      .from('threads')
      .update({ status: 'closed' as const, updated_at: new Date().toISOString() })
      .eq('id', threadId);

    if (error) return { error: error.message };
    return { error: null };
  };

  const reopenThread = async (threadId: string) => {
    const { error } = await supabase
      .from('threads')
      .update({ status: 'open' as const, updated_at: new Date().toISOString() })
      .eq('id', threadId);

    if (error) return { error: error.message };
    return { error: null };
  };

  const deleteThread = async (threadId: string) => {
    const { error } = await supabase
      .from('threads')
      .delete()
      .eq('id', threadId);

    if (error) return { error: error.message };
    return { error: null };
  };

  return {
    threads,
    loading,
    createThread,
    closeThread,
    reopenThread,
    deleteThread,
    refresh: fetchThreads,
  };
};

export const useThreadMessages = (threadId: string | null) => {
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [thread, setThread] = useState<ThreadRow | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchMessages = useCallback(async () => {
    if (!threadId || !user) {
      setLoading(false);
      return;
    }

    const [threadRes, messagesRes] = await Promise.all([
      supabase.from('threads').select('*').eq('id', threadId).single(),
      supabase
        .from('thread_messages')
        .select('*')
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true }),
    ]);

    if (threadRes.data) setThread(threadRes.data);
    if (messagesRes.data) {
      setMessages(
        messagesRes.data.map((msg) => ({ ...msg, author_email: null }))
      );
    }
    setLoading(false);
  }, [threadId, user]);

  useEffect(() => {
    setLoading(true);
    fetchMessages();
  }, [fetchMessages]);

  // Realtime dla wiadomosci w watku
  useEffect(() => {
    if (!threadId || !user) return;

    const channel = supabase
      .channel(`thread-${threadId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'thread_messages',
          filter: `thread_id=eq.${threadId}`,
        },
        () => {
          fetchMessages();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'threads',
          filter: `id=eq.${threadId}`,
        },
        () => {
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [threadId, user, fetchMessages]);

  const sendMessage = async (content: string) => {
    if (!user || !threadId) return { error: 'Brak watku lub uzytkownika' };

    const { error: msgError } = await supabase.from('thread_messages').insert({
      thread_id: threadId,
      user_id: user.id,
      content,
    });

    if (msgError) return { error: msgError.message };

    // Aktualizuj updated_at watku
    await supabase
      .from('threads')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', threadId);

    return { error: null };
  };

  return {
    thread,
    messages,
    loading,
    sendMessage,
    refresh: fetchMessages,
  };
};
